import React, { useState, useEffect } from 'react';
import { MAPPING_CATEGORIES } from '../constants/mappingConstants';
import { useToast } from './ToastContainer';
import { useFetchMutation } from '../hooks/useFetchAuth';
import { Calculator } from 'lucide-react';
import LoadCheckPanel from './LoadCheckPanel';

const PSEUDO_COLUMNS = ['_client', '_shop_sign', '_year', '_week'];

function SummaryStep({ sheetConfigs, onSubmit, mappingChoices, fileName, shopSign, client, year, week, category, error }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const { addToast } = useToast();
  
  const { mutateAsync: fetchPreviewMutation, isPending: previewLoading } = useFetchMutation();
  const { mutateAsync: fetchLoadCheck, isPending: checkLoading } = useFetchMutation();
  const [loadCheck, setLoadCheck] = useState(null);
  const [loadCheckError, setLoadCheckError] = useState(null);
  const [anomaliesConfirmed, setAnomaliesConfirmed] = useState(false);

  useEffect(() => {
    if (error) {
      addToast({ title: 'Erreur lors de l\'importation', message: error, type: 'error', duration: 6000 });
    }
  }, [error]);

  const getConfiguredSheets = () => {
    return Object.entries(sheetConfigs).filter(([_, config]) => config.isProcessing);
  };

  const getMappingDescription = (mappingValue) => {
    return mappingChoices.find(choice => choice.value === mappingValue)?.description || mappingValue;
  };

  // Prepare the request data in the format expected by the backend, without the
  // _year/_week fields the import also leaves out (useProcessorState submitProcess)
  const buildRequestData = () => ({
    sheets: Object.entries(sheetConfigs)
      .filter(([_, config]) => config.isProcessing)
      .map(([sheetName, config]) => ({
        sheet: sheetName,
        mapping: Object.fromEntries(
          Object.entries(config.columnMappings || {}).filter(([column]) => !PSEUDO_COLUMNS.includes(column))
        ),
        isProcessing: config.isProcessing
      })),
    shop_sign: shopSign,
    client: client,
    year: year,
    week: week,
    category: category
  });

  const runLoadCheck = async () => {
    setLoadCheck(null);
    setLoadCheckError(null);
    setAnomaliesConfirmed(false);
    try {
      setLoadCheck(await fetchLoadCheck({
        url: `/api/load-check/${fileName}/`,
        method: 'POST',
        body: buildRequestData()
      }));
    } catch (error) {
      setLoadCheckError(error.message);
    }
  };

  const fetchPreview = async () => {
    setPreviewError(null);

    try {
      const requestData = buildRequestData();

      const data = await fetchPreviewMutation({
        url: `/api/preview-mapped/${fileName}/`,
        method: 'POST',
        body: requestData
      });

      setPreviewData(data);
    } catch (error) {
      console.error('Error fetching preview:', error);
      setPreviewError(error.message);
    }
  };

  useEffect(() => {
    if (fileName && getConfiguredSheets().length > 0) {
      fetchPreview();
    }
  }, [fileName]);

  // Again when the mapping changes: the anomalies confirmed were those of the previous mapping
  const checkRequest = JSON.stringify(buildRequestData());
  useEffect(() => {
    if (fileName && getConfiguredSheets().length > 0) {
      runLoadCheck();
    }
  }, [fileName, checkRequest]);

  const anomalies = loadCheck?.checked
    ? [...loadCheck.file_anomalies, ...loadCheck.sheets.flatMap((sheet) => sheet.anomalies)]
    : [];
  // An anomalous file is loaded only once the user confirms it; a failed check does not block
  const blockedByCheck = checkLoading || (anomalies.length > 0 && !anomaliesConfirmed);

  const handleSubmit = async () => {
    setIsProcessing(true);

    try {
      // Kept with the job, to know later that the user saw these anomalies
      await onSubmit(anomalies.map((anomaly) => (anomaly.sheet ? `${anomaly.sheet} · ` : '') + anomaly.message));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold dark:text-white">Récapitulatif de l'import</h2>
      </div>

      <div className="space-y-6">
        {getConfiguredSheets().map(([sheetName, config]) => (
          <div
            key={sheetName}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Feuille : {sheetName}
            </h3>

            <div className="space-y-4">
              {Object.entries(MAPPING_CATEGORIES).map(([category, categoryConfig]) => {
                const mappings = Object.entries(config.columnMappings)
                  .filter(([_, mappingValue]) => {
                    const choice = mappingChoices.find(c => c.value === mappingValue);
                    if (category === 'required') return choice?.required;
                    if (category === 'important') return choice?.important && !choice?.required;
                    return !choice?.important && !choice?.required;
                  });

                if (mappings.length === 0) return null;

                return (
                  <div
                    key={category}
                    className={`p-3 rounded-lg ${categoryConfig.bgColor} ${categoryConfig.borderColor}`}
                  >
                    <h4 className={`text-sm font-medium ${categoryConfig.titleColor} mb-2`}>
                      {categoryConfig.title}
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {mappings.map(([column, mappingValue]) => (
                        <div
                          key={column}
                          className="flex items-center justify-center bg-white dark:bg-gray-700
                                   rounded px-3 py-2 text-sm gap-2"
                        >
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {mappingValue}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400">
                            ← {column}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Preview Section */}
      <div className="mt-8 space-y-6">
        <h2 className="text-lg font-semibold dark:text-white">Aperçu des données mappées</h2>

        {previewLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="ml-3 text-gray-600">Chargement de l'aperçu...</p>
          </div>
        )}

        {previewError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">Erreur lors du chargement de l'aperçu: {previewError}</p>
          </div>
        )}

        {previewData && !previewLoading && (
          <div className="space-y-6">
            {Object.entries(previewData).map(([sheetName, sheetData]) => (
              <div key={sheetName} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Aperçu - Feuille: {sheetName}
                </h3>
                
                {sheetData.metric_sums && Object.keys(sheetData.metric_sums).length > 0 && (
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-gray-700/50 rounded-lg border border-blue-100 dark:border-gray-600">
                    <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-3 flex items-center gap-2">
                      <Calculator className="w-4 h-4" />
                      Totaux des métriques
                    </h4>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(sheetData.metric_sums).map(([metric, value]) => (
                        <div key={metric} className="flex flex-col bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                            {metric.replace(/_/g, ' ')}
                          </span>
                          <span className="text-lg font-bold text-gray-900 dark:text-white font-mono">
                            {typeof value === 'number' 
                              ? value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })
                              : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="overflow-auto max-h-[300px] scroll-bar">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 ">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        {sheetData.columns?.map((column) => (
                          <th
                            key={column}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {sheetData.sample_rows?.map((row, index) => (
                        <tr key={index}>
                          {sheetData.columns?.map((column) => (
                            <td
                              key={column}
                              className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100"
                            >
                              {row[column] ?? 'N/A'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Affichage de {sheetData.sample_rows?.length || 0} lignes d'exemple
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8">
        <LoadCheckPanel
          check={loadCheck}
          loading={checkLoading}
          error={loadCheckError}
          confirmed={anomaliesConfirmed}
          onConfirm={setAnomaliesConfirmed}
        />
      </div>

      <div className="mt-4 flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={isProcessing || blockedByCheck}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                     transition-colors duration-200 flex items-center gap-2
                     disabled:bg-blue-400 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Import en cours...
            </>
          ) : (
            <>
              {anomalies.length > 0 ? 'Charger quand même' : 'Lancer l\'import'}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default SummaryStep;